import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import axios from "axios";
import MicrosoftSSOLogin from "./MicrosoftSSO";
import useApplicationData from "../../hooks/useApplicationData";

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: "0 20px 20px 20px",
  marginTop: "10vh",
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

export default function SignInForm(props) {
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [loginError, setLoginError] = useState("");
  const [microsofUser, setMicrosoftUser] = useState(null);
  const navigate = useNavigate();
  const {
    fetchApplicationData,
    fetchTotalApplicationsCount,
    fetchUniqueValues,
  } = useApplicationData();

  useEffect(() => {
    if (microsofUser) {
      login(microsofUser.email, microsofUser.password);
    }
  }, [microsofUser]);

  const handleClickOpen = () => {
    // logic to handle forgot password
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
  };

  const login = async (email, password) => {
    const existingUserData =
      JSON.parse(localStorage.getItem("userDetails")) || {};

    // Step 2: Update fields
    const updatedUserData = {
      ...existingUserData,
      name: email, // Or any updated fields
    };

    // Step 3: Save back to localStorage
    localStorage.setItem("userDetails", JSON.stringify(updatedUserData));
    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);

      const response = await axios.post(
        "https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/v1/login",
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
        }
      );

      const { access_token, refresh_token } = response.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      await fetchApplicationData(access_token, refresh_token);
      await fetchTotalApplicationsCount(access_token, refresh_token);
      await fetchUniqueValues(access_token, refresh_token);

      navigate("/app/details");
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail || "Login failed. Please try again.";
      setLoginError("Incorrect username or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const validateInputs = () => {
    const email = document.getElementById("email");
    const password = document.getElementById("password");

    let isValid = true;

    if (!email.value) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Password must be at least 6 characters long.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    if (isValid) {
      login(email.value, password.value);
    }
  };

  return (
    <>
      <SignInContainer direction="column" justifyContent="space-between">
        <div className="flex m-auto login-wrapper">
          <div className="flex items-center justify-center login-form">
            <div className="w-96">
              <Typography
                component="h3"
                variant="h4"
                sx={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "26px",
                  fontWeight: "600",
                  marginBottom: "12px",
                }}
              >
                Sign In
              </Typography>
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >
                <FormControl>
                  <FormLabel htmlFor="email">User Name</FormLabel>
                  <TextField
                    error={emailError}
                    helperText={emailErrorMessage}
                    id="email"
                    type="email"
                    name="email"
                    placeholder="your@email.com"
                    autoComplete="email"
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                    color={emailError ? "error" : "primary"}
                    sx={{ ariaLabel: "email" }}
                    className="form-input"
                  />
                </FormControl>
                <FormControl>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <FormLabel htmlFor="password">Password</FormLabel>
                  </Box>
                  <TextField
                    error={passwordError}
                    helperText={passwordErrorMessage}
                    name="password"
                    placeholder="••••••"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                    color={passwordError ? "error" : "primary"}
                    className="form-input"
                  />
                  <div className="text-right">
                    <Link
                      component="button"
                      onClick={handleClickOpen}
                      variant="body2"
                      sx={{ alignSelf: "baseline" }}
                      className="text-right w-100"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                </FormControl>
                <FormControlLabel
                  control={<Checkbox value="remember" color="primary" />}
                  label="Remember me"
                  className="remember"
                  style={{ marginLeft: "-7px", height: "25px" }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  onClick={validateInputs}
                  style={{
                    backgroundColor: "#FF4D00",
                    boxShadow: "none",
                    color: "#fff",
                    padding: "8px",
                  }}
                >
                  Log In
                </Button>
                <Typography sx={{ textAlign: "center" }}>
                  Don&apos;t have an account?{" "}
                  <span>
                    <Link
                      href="/material-ui/getting-started/templates/sign-in/"
                      variant="body2"
                      sx={{ alignSelf: "center" }}
                    >
                      Sign Up
                    </Link>
                  </span>
                </Typography>
              </Box>

              <MicrosoftSSOLogin
                setMicrosoftUser={setMicrosoftUser}
                className="micro-soft"
              />
            </div>
          </div>
          <div className="login-img">
            <img src="/img/login-img.png" alt="login" width={"450px"} />
          </div>
        </div>
      </SignInContainer>

      <Backdrop
        open={loading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: "#ea580c",
          }}
        />
      </Backdrop>
    </>
  );
}
